import { Pencil, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from '@/components/ui';
import {
  useAllCustomCities,
  useDeleteCustomCity,
  useUpdateCustomCity,
} from '@/hooks/useCustomCities';
import type { CustomCityRow } from '@/services/locations.service';

const TH =
  'whitespace-nowrap px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-ink/40';
const TD = 'whitespace-nowrap px-5 py-3.5 text-sm';

const RenameCityDialog = ({
  row,
  open,
  onOpenChange,
}: {
  row: CustomCityRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { toast } = useToast();
  const updateCity = useUpdateCustomCity();
  const [name, setName] = useState('');

  useEffect(() => {
    if (open && row) setName(row.city);
  }, [open, row]);

  const handleSave = (): void => {
    if (!row) return;
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Enter a city name');
      return;
    }
    updateCity.mutate(
      { id: row.id, city: trimmed },
      {
        onSuccess: () => {
          toast.success('City updated');
          onOpenChange(false);
        },
        onError: () => toast.error('Could not update the city'),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Rename city</DialogTitle>
          <DialogDescription>
            Fix a typo for <span className="font-semibold">{row?.state}</span>. If the corrected
            name already exists for this state, the two are merged. Existing leads keep their stored
            value.
          </DialogDescription>
        </DialogHeader>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="City name"
          className="w-full rounded-md border border-line bg-white px-3 py-2 text-sm shadow-sm placeholder:text-ink/40 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
        />
        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-md border border-line bg-white px-4 py-1.5 text-sm font-semibold text-ink hover:bg-cream"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={updateCity.isPending}
            className="rounded-md bg-brand px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-50"
          >
            {updateCity.isPending ? 'Saving…' : 'Save'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DeleteCityDialog = ({
  row,
  open,
  onOpenChange,
}: {
  row: CustomCityRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { toast } = useToast();
  const deleteCity = useDeleteCustomCity();

  const handleDelete = (): void => {
    if (!row) return;
    deleteCity.mutate(row.id, {
      onSuccess: () => {
        toast.success('City removed');
        onOpenChange(false);
      },
      onError: () => toast.error('Could not remove the city'),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Remove city</DialogTitle>
          <DialogDescription>
            Remove <span className="font-semibold">{row?.city}</span> from the{' '}
            <span className="font-semibold">{row?.state}</span> dropdown? It will no longer be
            suggested for new leads. Existing leads are unaffected.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-md border border-line bg-white px-4 py-1.5 text-sm font-semibold text-ink hover:bg-cream"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleteCity.isPending}
            className="rounded-md bg-danger px-4 py-1.5 text-sm font-semibold text-white hover:bg-danger/90 disabled:opacity-50"
          >
            {deleteCity.isPending ? 'Removing…' : 'Remove'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AdminCustomCitiesPage = () => {
  const { data: rows = [], isLoading, isError } = useAllCustomCities();
  const [renaming, setRenaming] = useState<CustomCityRow | null>(null);
  const [deleting, setDeleting] = useState<CustomCityRow | null>(null);

  return (
    <div>
      <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">Custom cities</h1>
      <p className="mt-1.5 text-sm text-ink/60">
        Cities Sales RMs added on the fly while creating leads. They appear in the City dropdown for
        that state. Rename a typo (merges into an existing name) or remove one entirely. The bundled
        base cities are not listed here and can&apos;t be edited.
      </p>

      <div className="mt-6 overflow-hidden rounded-xl border border-line bg-white shadow-sm">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-line bg-cream/60">
              <th className={TH}>State</th>
              <th className={TH}>City</th>
              <th className={TH}>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {isLoading && (
              <tr>
                <td colSpan={3} className="px-5 py-12 text-center text-sm text-ink/50">
                  Loading…
                </td>
              </tr>
            )}
            {isError && !isLoading && (
              <tr>
                <td colSpan={3} className="px-5 py-12 text-center text-sm text-danger">
                  Failed to load custom cities.
                </td>
              </tr>
            )}
            {!isLoading && !isError && rows.length === 0 && (
              <tr>
                <td colSpan={3} className="px-5 py-12 text-center text-sm text-ink/50">
                  No custom cities have been added yet.
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={row.id} className="transition-colors hover:bg-cream/50">
                <td className={`${TD} text-ink/70`}>{row.state}</td>
                <td className={`${TD} font-semibold text-ink`}>{row.city}</td>
                <td className={TD}>
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={() => setRenaming(row)}
                      className="flex items-center gap-1 font-semibold text-brand transition-colors hover:text-brand-hover"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Rename
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleting(row)}
                      className="flex items-center gap-1 font-semibold text-danger transition-colors hover:text-danger/80"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Remove
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <RenameCityDialog
        row={renaming}
        open={renaming !== null}
        onOpenChange={(open) => {
          if (!open) setRenaming(null);
        }}
      />
      <DeleteCityDialog
        row={deleting}
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      />
    </div>
  );
};

export default AdminCustomCitiesPage;
